import { mapGetters, mapActions } from 'vuex';

export default {
  data() {
    return {
      statusEffectsPolling: null,
      statusEffectsUpdateInterval: 30000 // 30 segundos
    };
  },

  computed: {
    ...mapGetters('statusEffects', [
      'activePlayerBuffs',
      'activePlayerDebuffs', 
      'playerStatusCount',
      'activeFighterBuffs',
      'activeFighterDebuffs',
      'fighterStatusCount',
      'getBuffConfig',
      'getDebuffConfig',
      'getFighterEnhancedStats'
    ]),

    // Helper computed properties
    hasPlayerStatusEffects() {
      return this.playerStatusCount.buffs > 0 || this.playerStatusCount.debuffs > 0;
    },

    playerStatusSummary() {
      return {
        buffs: this.activePlayerBuffs.length,
        debuffs: this.activePlayerDebuffs.length,
        total: this.activePlayerBuffs.length + this.activePlayerDebuffs.length
      };
    }
  },

  methods: {
    ...mapActions('statusEffects', [
      'fetchPlayerBuffs',
      'fetchPlayerDebuffs', 
      'fetchFighterBuffs',
      'fetchFighterDebuffs',
      'fetchFighterEnhancedStats',
      'loadPlayerStatusEffects',
      'loadFighterStatusEffects',
      'curePlayerDebuff',
      'cureFighterDebuff'
    ]),

    // Helper methods
    getFighterStatusSummary(fighterId) {
      const buffs = this.activeFighterBuffs(fighterId) || [];
      const debuffs = this.activeFighterDebuffs(fighterId) || [];
      
      return {
        buffs: buffs.length,
        debuffs: debuffs.length,
        total: buffs.length + debuffs.length
      };
    },

    getStatusEffectIcon(effectType, isDebuff = false) {
      const config = isDebuff 
        ? this.getDebuffConfig(effectType) 
        : this.getBuffConfig(effectType);
      return config?.icon || 'help';
    },

    getStatusEffectColor(effectType, isDebuff = false) {
      const config = isDebuff 
        ? this.getDebuffConfig(effectType) 
        : this.getBuffConfig(effectType);
      return config?.color || '#666666';
    },

    getStatusEffectName(effectType, isDebuff = false) {
      const config = isDebuff 
        ? this.getDebuffConfig(effectType) 
        : this.getBuffConfig(effectType);
      return config?.name || effectType;
    },

    formatTimeRemaining(expiresAt) {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diffMs = expiry.getTime() - now.getTime();
      
      if (diffMs <= 0) return '0min';
      
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 0) {
        return `${diffDays}d ${diffHours % 24}h`;
      } else if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes % 60}min`;
      } else {
        return `${diffMinutes}min`;
      }
    },

    formatEffectValue(effectValue) {
      // Convert effect value to percentage display
      if (effectValue > 1) {
        // Buff - show as increase (e.g., 1.5 = +50%)
        const percentage = Math.round((effectValue - 1) * 100);
        return `+${percentage}%`;
      } else {
        // Debuff - show as decrease (e.g., 0.7 = -30%)
        const percentage = Math.round((1 - effectValue) * 100);
        return `-${percentage}%`;
      }
    },

    isExpiringSoon(expiresAt, thresholdMinutes = 5) {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diffMs = expiry.getTime() - now.getTime();
      const diffMinutes = diffMs / (1000 * 60);
      
      return diffMinutes > 0 && diffMinutes <= thresholdMinutes;
    },

    // Cure methods with user feedback
    async curePlayerDebuffWithFeedback(debuffId) {
      try {
        const result = await this.curePlayerDebuff(debuffId);
        
        if (result.success) {
          this.$notify({
            type: 'success',
            title: 'Debuff Curado!',
            text: `Debuff curado com sucesso por ${result.cost} coins`
          });
          
          // Refresh player status effects
          await this.loadPlayerStatusEffects();
        }
        
        return result;
      } catch (error) {
        this.$notify({
          type: 'error', 
          title: 'Erro ao Curar',
          text: error.message || 'Não foi possível curar o debuff'
        });
        throw error;
      }
    },

    async cureFighterDebuffWithFeedback(fighterId, debuffId) {
      try {
        const result = await this.cureFighterDebuff({ fighterId, debuffId });
        
        if (result.success) {
          this.$notify({
            type: 'success',
            title: 'Debuff Curado!',
            text: `Debuff do fighter curado por ${result.cost} coins`
          });
          
          // Refresh fighter status effects
          await this.loadFighterStatusEffects(fighterId);
        }
        
        return result;
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Erro ao Curar',
          text: error.message || 'Não foi possível curar o debuff'
        });
        throw error;
      }
    },

    // Polling methods
    startStatusEffectsPolling() {
      this.stopStatusEffectsPolling(); // Clear any existing interval
      
      this.statusEffectsPolling = setInterval(async () => {
        try {
          await this.loadPlayerStatusEffects();
        } catch (error) {
          console.warn('Error during status effects polling:', error);
        }
      }, this.statusEffectsUpdateInterval);
    },

    stopStatusEffectsPolling() {
      if (this.statusEffectsPolling) {
        clearInterval(this.statusEffectsPolling);
        this.statusEffectsPolling = null;
      }
    },

    startFighterStatusEffectsPolling(fighterId) {
      // Create unique polling for specific fighter
      const pollingKey = `fighter_${fighterId}`;
      
      if (this[pollingKey]) {
        clearInterval(this[pollingKey]);
      }
      
      this[pollingKey] = setInterval(async () => {
        try {
          await this.loadFighterStatusEffects(fighterId);
        } catch (error) {
          console.warn(`Error during fighter ${fighterId} status effects polling:`, error);
        }
      }, this.statusEffectsUpdateInterval);
    },

    stopFighterStatusEffectsPolling(fighterId) {
      const pollingKey = `fighter_${fighterId}`;
      
      if (this[pollingKey]) {
        clearInterval(this[pollingKey]);
        delete this[pollingKey];
      }
    }
  },

  // Lifecycle hooks
  created() {
    // Auto-load player status effects when mixin is used
    this.loadPlayerStatusEffects();
  },

  mounted() {
    // Start polling for status updates
    this.startStatusEffectsPolling();
  },

  beforeUnmount() {
    // Cleanup polling on component destroy
    this.stopStatusEffectsPolling();
    
    // Clean up any fighter-specific polling
    Object.keys(this.$data).forEach(key => {
      if (key.startsWith('fighter_') && this[key]) {
        clearInterval(this[key]);
      }
    });
  }
};